import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isLoggedIn, logout } from '../services/authService';

interface HeaderProps {
  title?: string;
  showBack?: boolean;
}

const Header: React.FC<HeaderProps> = ({ title, showBack = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const isUserLoggedIn = isLoggedIn();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Main Navigation Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="text-xl font-bold text-minto-600">
                Minto
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-gray-600 hover:text-minto-600">
                Home
              </Link>
              {/* Repair Areas Dropdown */}
              <div className="relative group">
                <button className="flex items-center gap-1 text-gray-600 hover:text-minto-600 transition-colors">
                  Repair Areas
                  <i className="fas fa-chevron-down text-xs mt-0.5"></i>
                </button>
                <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-3 w-96 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                  <div className="grid grid-cols-3 gap-0 divide-x divide-gray-100">
                    {/* Western Line */}
                    <div className="px-3">
                      <p className="text-xs font-bold text-minto-500 uppercase tracking-wide mb-2">Western Line</p>
                      {[
                        { to: '/mobile-repair-lower-parel', label: 'Lower Parel' },
                        { to: '/mobile-repair-dadar', label: 'Dadar' },
                        { to: '/mobile-repair-bandra', label: 'Bandra' },
                        { to: '/mobile-repair-vile-parle', label: 'Vile Parle' },
                        { to: '/mobile-repair-andheri', label: 'Andheri' },
                        { to: '/mobile-repair-goregaon', label: 'Goregaon' },
                        { to: '/mobile-repair-malad', label: 'Malad' },
                        { to: '/mobile-repair-borivali', label: 'Borivali' },
                      ].map((a) => (
                        <Link key={a.to} to={a.to} className="block py-1 text-xs text-gray-600 hover:text-minto-600 transition-colors">
                          {a.label}
                        </Link>
                      ))}
                    </div>
                    {/* Central Line */}
                    <div className="px-3">
                      <p className="text-xs font-bold text-minto-500 uppercase tracking-wide mb-2">Central Line</p>
                      {[
                        { to: '/mobile-repair-kurla', label: 'Kurla' },
                        { to: '/mobile-repair-ghatkopar', label: 'Ghatkopar' },
                        { to: '/mobile-repair-thane', label: 'Thane' },
                      ].map((a) => (
                        <Link key={a.to} to={a.to} className="block py-1 text-xs text-gray-600 hover:text-minto-600 transition-colors">
                          {a.label}
                        </Link>
                      ))}
                    </div>
                    {/* Harbour Line */}
                    <div className="px-3">
                      <p className="text-xs font-bold text-minto-500 uppercase tracking-wide mb-2">Harbour Line</p>
                      {[
                        { to: '/mobile-repair-chembur', label: 'Chembur' },
                        { to: '/mobile-repair-navi-mumbai', label: 'Navi Mumbai' },
                      ].map((a) => (
                        <Link key={a.to} to={a.to} className="block py-1 text-xs text-gray-600 hover:text-minto-600 transition-colors">
                          {a.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <Link to="/about" className="text-gray-600 hover:text-minto-600">
                About Us
              </Link>
              <Link to="/faq" className="text-gray-600 hover:text-minto-600">
                FAQ
              </Link>
              {isUserLoggedIn ? (
                <>
                  <Link to="/orders" className="text-gray-600 hover:text-minto-600">
                    My Orders
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="text-gray-600 hover:text-minto-600"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="px-4 py-2 rounded-lg bg-minto-500 text-white hover:bg-minto-600 transition-colors"
                >
                  Login
                </Link>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <i className={`fas ${isMenuOpen ? 'fa-times' : 'fa-bars'} text-gray-600`}></i>
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-100">
              <nav className="flex flex-col space-y-3">
                <Link
                  to="/"
                  className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Home
                </Link>
                {/* Repair Areas */}
                <div className="px-4 pt-1">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Western Line</p>
                  <div className="grid grid-cols-3 gap-1 mb-3">
                    {[
                      { to: '/mobile-repair-lower-parel', label: 'Lower Parel' },
                      { to: '/mobile-repair-dadar', label: 'Dadar' },
                      { to: '/mobile-repair-bandra', label: 'Bandra' },
                      { to: '/mobile-repair-vile-parle', label: 'Vile Parle' },
                      { to: '/mobile-repair-andheri', label: 'Andheri' },
                      { to: '/mobile-repair-goregaon', label: 'Goregaon' },
                      { to: '/mobile-repair-malad', label: 'Malad' },
                      { to: '/mobile-repair-borivali', label: 'Borivali' },
                    ].map((area) => (
                      <Link key={area.to} to={area.to}
                        className="px-2 py-1.5 text-xs text-gray-600 hover:bg-minto-50 hover:text-minto-600 rounded-lg transition-colors text-center"
                        onClick={() => setIsMenuOpen(false)}>
                        {area.label}
                      </Link>
                    ))}
                  </div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Central Line</p>
                  <div className="grid grid-cols-3 gap-1 mb-3">
                    {[
                      { to: '/mobile-repair-kurla', label: 'Kurla' },
                      { to: '/mobile-repair-ghatkopar', label: 'Ghatkopar' },
                      { to: '/mobile-repair-thane', label: 'Thane' },
                    ].map((area) => (
                      <Link key={area.to} to={area.to}
                        className="px-2 py-1.5 text-xs text-gray-600 hover:bg-minto-50 hover:text-minto-600 rounded-lg transition-colors text-center"
                        onClick={() => setIsMenuOpen(false)}>
                        {area.label}
                      </Link>
                    ))}
                  </div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Harbour Line</p>
                  <div className="grid grid-cols-3 gap-1">
                    {[
                      { to: '/mobile-repair-chembur', label: 'Chembur' },
                      { to: '/mobile-repair-navi-mumbai', label: 'Navi Mumbai' },
                    ].map((area) => (
                      <Link key={area.to} to={area.to}
                        className="px-2 py-1.5 text-xs text-gray-600 hover:bg-minto-50 hover:text-minto-600 rounded-lg transition-colors text-center"
                        onClick={() => setIsMenuOpen(false)}>
                        {area.label}
                      </Link>
                    ))}
                  </div>
                </div>
                <Link
                  to="/about"
                  className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                  onClick={() => setIsMenuOpen(false)}
                >
                  About Us
                </Link>
                <Link
                  to="/faq"
                  className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                  onClick={() => setIsMenuOpen(false)}
                >
                  FAQ
                </Link>
                <Link
                  to="/privacy-policy"
                  className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/terms-conditions"
                  className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Terms & Conditions
                </Link>
                <Link
                  to="/refunds-cancellations"
                  className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Refunds & Cancellations
                </Link>
                {isUserLoggedIn ? (
                  <>
                    <Link
                      to="/orders"
                      className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      My Orders
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="px-4 py-2 text-left text-gray-600 hover:bg-gray-50 rounded-lg"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Login
                  </Link>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Sub-header with back button and title */}
      {title && (
        <div className="bg-white border-b border-gray-200">
          <div className="container mx-auto px-4">
            <div className="flex items-center h-14">
              {showBack && (
                <button
                  onClick={() => navigate(-1)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 mr-3"
                >
                  <i className="fas fa-arrow-left text-gray-600"></i>
                </button>
              )}
              <h1 className="text-lg font-medium text-gray-900">{title}</h1>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header; 